// Clinical constants shared across the back-office surface.
//
// Source of truth — these are NOT seed data. They mirror:
//   - ELATION_BOOKING_URL: WF33 `Parse & Validate` (DEFAULT_ELATION_URL — an OVERRIDABLE default,
//     not a pin: `payload.elation_url || DEFAULT_ELATION_URL`), WF69 `Decide Actions` (BOOKING_URL)
//     AND WF69 `Decide Notify` (a second copy re-inlined in its SMS template), WF2
//     `Parse Gemini Task Response` (the booking-link nudge append). Static `S-157` DERIVES the copy
//     set by scanning, so a NEW copy must AGREE rather than merely exist.
//   - CKM_BASELINE_LAB_PANEL: WF31 lab-order test selection
//   - CONDITION_TO_ICD10: WF16 (Elation patient setup mapping)
//
// Update here AND the matching workflow JSON when these change.

/**
 * Hardcoded Elation booking URL for one-click appointment requests.
 * Update this constant AND every workflow copy in the same commit (see the header) — `S-157` fails
 * on any drift. It is a LITERAL everywhere on purpose and must never become an n8n `$vars`: a
 * project variable is editable in the UI with no diff, no review and no CI visibility, and this URL
 * goes into unsolicited patient SMS from WF2, WF33 and WF69.
 * The app's only consumer is an optimistic echo of WF33's message in `ChatPanel` — not a send path.
 */
export const ELATION_BOOKING_URL = 'https://within.gs/clinic-appointments';

/** Per-kind booking links (spec 2026-10-26-recurring-virtual-visits D12), each landing on an Elation page pinned to
 *  one appointment type. Literals like ELATION_BOOKING_URL, under the same S-157 rule; WF33 and WF69 carry copies. */
export const CC_BOOKING_URL = 'https://within.gs/medical-welcome-call';
export const NP_BOOKING_URL = 'https://within.gs/medical-virtual-visit';

/**
 * Elation EHR patient-chart base. Deep links are `${ELATION_PATIENT_BASE_URL}/<ehr_patient_id>/`.
 * Prod host is `app.elationemr.com` (sandbox is `sandbox.elationemr.com`).
 */
export const ELATION_PATIENT_BASE_URL = 'https://app.elationemr.com/patient';

/**
 * Recommended baseline CKM lab panel (LOINCs + display names).
 * Used as default test selection for `onb_baseline_labs_order` and
 * `rpt_final_labs_order`. The care team can check/uncheck individual tests.
 */
export const CKM_BASELINE_LAB_PANEL = [
  { loinc: '13457-7', display: 'LDL Cholesterol (calc)' },
  { loinc: '2093-3', display: 'Total Cholesterol' },
  { loinc: '2085-9', display: 'HDL Cholesterol' },
  { loinc: '2571-8', display: 'Triglycerides' },
  { loinc: '4548-4', display: 'Hemoglobin A1c' },
  { loinc: '48642-3', display: 'eGFR (CKD-EPI)' },
  { loinc: '14959-1', display: 'uACR (urine albumin/creatinine)' },
  { loinc: '2345-7', display: 'Glucose (serum)' },
  { loinc: '2160-0', display: 'Creatinine (serum)' },
  { loinc: '2823-3', display: 'Potassium (serum)' },
  { loinc: '2951-2', display: 'Sodium (serum)' },
] as const;

/**
 * Map ACCESS condition labels → ICD-10 codes for lab ordering.
 * Mirrors the mapping used in WF16 (Elation patient setup).
 */
export const CONDITION_TO_ICD10: Record<string, string> = {
  'High blood pressure': 'I10',
  Hypertension: 'I10',
  'Type 2 diabetes': 'E11.9',
  'Type 2 Diabetes': 'E11.9',
  'Type 1 diabetes': 'E10.9',
  Obesity: 'E66.9',
  'Heart disease or heart failure': 'I50.9',
  'High cholesterol': 'E78.5',
  'Kidney disease': 'N18.9',
};
